import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DEFAULT_SORT_OPTIONS, getLongestSortLabel, SortOptionId } from '../utils/sortOptions';
import { DROPDOWN_SORT_LEFT_LABEL } from '../constants/uiText';

type SortOption = { id: SortOptionId; label: string };

interface Props {
  options?: SortOption[];
  selected?: SortOptionId;
  onChange?: (id: SortOptionId) => void;
  labelLeft?: string;
}

const SortDropdownButton = ({ 
    options, 
    selected, 
    onChange, 
    labelLeft = DROPDOWN_SORT_LEFT_LABEL, 
}: Props) => {
  const sortOpts = options ?? DEFAULT_SORT_OPTIONS;

  const [currentSort, setCurrentSort] = useState(selected ?? sortOpts[0].id);
  const [open, setOpen] = useState(false);

  const sizerRef = useRef<HTMLSpanElement | null>(null);
  const [sortBtnMinWidth, setSortBtnMinWidth] = useState<number>(0);

  const longestLabel = getLongestSortLabel(sortOpts);
  const currentLabel = sortOpts.find((o) => o.id === currentSort)?.label ?? sortOpts[0].label;

  useLayoutEffect(() => {
    if (!sizerRef.current) return;
    setSortBtnMinWidth(sizerRef.current.offsetWidth + 4);
  }, [longestLabel]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: Event) => {
      if (!(e.target as HTMLElement)?.closest('.sort-dropdown')) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (selected) setCurrentSort(selected);
  }, [selected]);


  const select = (id: SortOptionId) => {
    setCurrentSort(id);
    onChange?.(id);
    setOpen(false);
  };

  return (
    <div className="dropdow sort-dropdown">
      <SortDropdownToggle
        className="btn btn-outline-secondary dropdown-toggle"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          position: 'relative',
          minWidth: sortBtnMinWidth ? `${sortBtnMinWidth}px` : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingLeft: '2.5rem',
          whiteSpace: 'nowrap',
        }}
      >
        {/* hidden sizer used ONLY for measuring */}
        <span
          ref={sizerRef}
          className="btn btn-outline-secondary dropdown-toggle"
          style={{
            position: 'absolute',
            visibility: 'hidden',
            whiteSpace: 'nowrap',
            left: -9999,
            top: -9999,
            paddingLeft: '3.25rem',
          }}
        >
          <span
            style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            {labelLeft}
          </span>
          {longestLabel}
        </span>

        <span
          style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          {labelLeft}
        </span>

        <span style={{ flex: 1, textAlign: 'center' }}>
          {currentLabel}
        </span>
      </SortDropdownToggle>

      <ul
        className={`dropdown-menu${open ? ' show' : ''}`}
        style={{ display: open ? 'block' : 'none' }}
      >
        {sortOpts.map((opt) => (
          <li key={opt.id}>
            <button
              className="dropdown-item"
              type="button"
              onClick={() => {
                select(opt.id)
              }}
            >
              {opt.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SortDropdownToggle = styled.button`
  &.dropdown-toggle::after {
    margin-left: auto;
  }
`;

export default SortDropdownButton;
